import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { resolveUrl } from "@/components/ImageUploader";
import BrandLogo from "@/components/BrandLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import StatusBadge from "@/components/StatusBadge";
import WishlistButton from "@/components/WishlistButton";
import BrandStyle from "@/components/BrandStyle";
import { ArrowRight, ShoppingBag, User, Heart, Search, Instagram, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * Editorial Luxe template — premium concept ispirato a Cassina / B&B Italia.
 * Hero parallax full-bleed · tipografia serif XL · griglia asimmetrica · palette monocromatica con accento brand.
 */
export default function StorefrontHomeEditorialLuxe({ data, cms, bestSellers, newsletterEmail, setNewsletterEmail, subscribeNewsletter }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const cart = useCart();
  const { t } = useI18n();
  const { tenant, network, items } = data;
  const brand = tenant.settings?.brand_kit || {};
  const social = tenant.settings?.social_links || {};
  const primary = brand.primary_color || "#0F1B14";
  const accent = brand.secondary_color || "#C6A664";

  const heroSection = cms?.sections?.find((s) => s.type === "hero");
  const heroTitle = heroSection?.content?.title || brand.tagline || tenant.name;
  const heroSubtitle = heroSection?.content?.subtitle || t("store_collection_2026");
  const heroBg = heroSection?.content?.background_image || brand.hero_image || "https://images.unsplash.com/photo-1618220179428-22790b461013?w=2400";
  const heroCtaText = heroSection?.content?.cta_text || t("store_explore_collection");

  const categories = network.vertical_config?.categories || [];
  const [category, setCategory] = useState(null);
  const filtered = category ? items.filter((i) => i.categoria === category) : items;
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen text-white" data-testid="storefront-home" data-storefront="true"
      style={{ background: "#0a0a0a", "--brand-primary": primary, "--brand-accent": accent }}>
      <BrandStyle brand={brand} />

      {/* ─────── Header ─────── */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all" style={{ background: scrollY > 50 ? "rgba(10,10,10,0.92)" : "transparent", backdropFilter: scrollY > 50 ? "blur(12px)" : "none", borderBottom: scrollY > 50 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
        <div className="max-w-[1600px] mx-auto px-8 lg:px-14 h-20 flex items-center justify-between">
          <Link to={`/s/${slug}`} className="flex items-center gap-3" data-testid="luxe-logo">
            <BrandLogo
              logoUrl={brand.logo_url}
              fallbackText={tenant.name?.toUpperCase()}
              resolveUrl={resolveUrl}
              className="h-9"
              invert={true}
              textClassName="font-heading text-xl tracking-wide"
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-10 text-xs tracking-widest uppercase">
            <a href="#collezione" className="hover:text-[var(--brand-accent)] transition-colors">{t("store_collection")}</a>
            {categories.slice(0, 4).map((c) => <a key={c} href={`#cat-${c}`} className="hover:text-[var(--brand-accent)] transition-colors" data-testid={`luxe-nav-${c}`}>{c}</a>)}
            <a href="#contatti" className="hover:text-[var(--brand-accent)] transition-colors">{t("nav_contact")}</a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to={`/s/${slug}/search`} className="w-10 h-10 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="luxe-search"><Search className="w-4 h-4" /></Link>
            <Link to={user?.role === "customer" ? "/account" : "/login"} className="w-10 h-10 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="luxe-account"><User className="w-4 h-4" /></Link>
            <Link to={user?.role === "customer" ? "/account" : "/login"} className="w-10 h-10 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="luxe-wishlist"><Heart className="w-4 h-4" /></Link>
            <button data-testid="luxe-cart" onClick={() => cart.setOpen(true)} className="relative inline-flex items-center gap-2 px-5 h-10 rounded-full border border-white/20 hover:bg-white/10 transition-colors text-xs tracking-wider uppercase">
              <ShoppingBag className="w-4 h-4" /> {t("nav_cart")}{cart.count > 0 && <span className="ml-1" style={{ color: accent }}>({cart.count})</span>}
            </button>
          </div>
        </div>
      </header>

      {/* ─────── Hero with parallax ─────── */}
      <section ref={heroRef} className="relative h-screen overflow-hidden" data-testid="luxe-hero">
        <div className="absolute inset-0" style={{ transform: `translateY(${scrollY * 0.4}px) scale(${1 + scrollY * 0.0002})`, transition: "none" }}>
          <img src={resolveUrl(heroBg)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 35%, rgba(10,10,10,0.92) 100%)" }} />
        </div>
        <div className="relative h-full max-w-[1600px] mx-auto px-8 lg:px-14 flex flex-col justify-end pb-32">
          <div className="text-[10px] tracking-[0.4em] uppercase mb-8 opacity-80" style={{ color: accent }}>{network.display_name} · {t("store_collection_2026")}</div>
          <h1 className="font-heading font-light leading-[0.95] tracking-tight text-[clamp(3.5rem,9vw,9rem)] max-w-5xl mb-10" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>
            {heroTitle}
          </h1>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 max-w-5xl">
            <p className="text-lg lg:text-xl max-w-xl text-white/80 leading-relaxed">{heroSubtitle}</p>
            <a href="#collezione" className="group inline-flex items-center gap-3 text-sm tracking-widest uppercase border-b border-white/60 pb-2 hover:border-white transition-colors" data-testid="luxe-hero-cta">
              {heroCtaText}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
            </a>
          </div>
        </div>
        {/* Decorative line */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-px h-12 bg-white/30 animate-pulse" />
      </section>

      {/* ─────── Manifesto ─────── */}
      <section className="py-32 lg:py-44 px-8 lg:px-14 max-w-[1600px] mx-auto" data-testid="luxe-manifesto">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-5">
            <div className="text-[10px] tracking-[0.4em] uppercase mb-6 text-white/50">{t("store_manifesto")}</div>
            <h2 className="font-heading font-light text-5xl lg:text-7xl leading-[1.05]" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>
              <span className="block">{t("store_luxe_h2_a")}</span>
              <span className="block italic" style={{ color: accent }}>{t("store_luxe_h2_b")}</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 space-y-4 text-white/70 text-lg leading-relaxed">
            <p>{t("store_luxe_p1", { brand: tenant.name })}</p>
            <p>{t("store_luxe_p2")}</p>
          </div>
        </div>
      </section>

      {/* ─────── Collezione (catalogo) ─────── */}
      <section id="collezione" className="px-8 lg:px-14 max-w-[1600px] mx-auto pb-32" data-testid="luxe-collection">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase mb-4 text-white/50">{t("store_catalog")}</div>
            <h2 className="font-heading font-light text-5xl lg:text-6xl" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>{t("store_our_collection")}</h2>
          </div>
          {categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <button onClick={() => setCategory(null)} data-testid="luxe-cat-all" className={`px-4 py-2 text-xs tracking-widest uppercase rounded-full transition-all ${!category ? "text-[var(--brand-accent)] border border-[var(--brand-accent)]" : "text-white/60 hover:text-white"}`}>{t("store_all")}</button>
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)} data-testid={`luxe-cat-${c}`} className={`px-4 py-2 text-xs tracking-widest uppercase rounded-full transition-all ${category === c ? "text-[var(--brand-accent)] border border-[var(--brand-accent)]" : "text-white/60 hover:text-white"}`}>{c}</button>
              ))}
            </div>
          )}
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-12 gap-6 lg:gap-8" data-testid="luxe-products-grid">
          {filtered.slice(0, 30).map((p, i) => {
            // Asymmetric layout: every 5th item is large
            const span = i % 5 === 0 ? "col-span-12 lg:col-span-8" : "col-span-12 sm:col-span-6 lg:col-span-4";
            const aspect = i % 5 === 0 ? "aspect-[16/10]" : "aspect-[4/5]";
            return (
              <Link key={p.sku_variante || p.id || i} to={`/s/${slug}/style/${p.sku_madre || p.sku_variante}`} data-testid={`luxe-prod-${p.sku_variante}`}
                className={`${span} group block`}>
                <div className={`relative ${aspect} overflow-hidden bg-neutral-900 mb-4`}>
                  {p.immagini?.[0] && <img src={resolveUrl(p.immagini[0])} alt={p.nome} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  <div className="absolute top-4 left-4">
                    <StatusBadge status={p.stato_disponibilita} />
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-white/70">{p.categoria}</div>
                    </div>
                    <WishlistButton sku={p.sku_variante} />
                  </div>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-heading font-light text-xl lg:text-2xl leading-tight truncate" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>{p.nome}</h3>
                    <div className="text-xs text-white/40 font-mono mt-0.5">{p.sku_variante}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {p.sconto_percentuale > 0 && <div className="text-xs text-white/40 line-through">€ {p.prezzo_originale}</div>}
                    <div className="text-lg" style={{ color: accent }}>€ {p.prezzo_finale}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ─────── Best sellers ─────── */}
      {bestSellers && bestSellers.length > 0 && (
        <section className="py-32 px-8 lg:px-14 border-t border-white/10" data-testid="luxe-bestsellers" style={{ background: `${primary}` }}>
          <div className="max-w-[1600px] mx-auto">
            <div className="text-[10px] tracking-[0.4em] uppercase mb-4 text-white/50">{t("store_highlight")}</div>
            <h2 className="font-heading font-light text-5xl lg:text-6xl mb-12" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>{t("store_bestsellers")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {bestSellers.slice(0, 4).map((p) => (
                <Link key={p.id || p.sku_variante} to={`/s/${slug}/style/${p.sku_madre || p.sku_variante}`} className="group" data-testid={`luxe-best-${p.sku_variante}`}>
                  <div className="aspect-square overflow-hidden bg-neutral-900 mb-3">
                    {p.immagini?.[0] && <img src={resolveUrl(p.immagini[0])} alt={p.nome} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                  </div>
                  <h3 className="font-heading font-light text-xl" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>{p.nome}</h3>
                  <div className="text-sm mt-1" style={{ color: accent }}>€ {p.prezzo_finale}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────── Newsletter / contatti ─────── */}
      <section id="contatti" className="py-32 px-8 lg:px-14 border-t border-white/10" data-testid="luxe-newsletter">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-6">
            <div className="text-[10px] tracking-[0.4em] uppercase mb-4 text-white/50">{t("store_newsletter_title")}</div>
            <h2 className="font-heading font-light text-5xl lg:text-6xl leading-[1.05] mb-6" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>
              {t("store_newsletter_lead")}
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 self-end">
            <form onSubmit={subscribeNewsletter} className="border-b border-white/30 pb-3 flex items-center gap-3">
              <Mail className="w-5 h-5 text-white/50" />
              <input type="email" required value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} data-testid="luxe-newsletter-input"
                placeholder={t("store_newsletter_email")} className="flex-1 bg-transparent outline-none text-white placeholder:text-white/40 py-2" />
              <button type="submit" data-testid="luxe-newsletter-submit" className="text-sm tracking-widest uppercase px-5 py-2 text-white hover:opacity-90 transition-opacity" style={{ background: primary }}>{t("store_newsletter_subscribe")} →</button>
            </form>
            <p className="text-xs text-white/40 mt-3">{t("store_newsletter_consent")}</p>
          </div>
        </div>
      </section>

      {/* ─────── Footer ─────── */}
      <footer className="border-t border-white/10 py-16 px-8 lg:px-14" data-testid="luxe-footer">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="font-heading text-2xl mb-3" style={{ fontFamily: brand.font_heading || "Cormorant Garamond" }}>{tenant.name}</div>
            <p className="text-sm text-white/50 max-w-sm">{brand.footer_text || t("store_footer_default")}</p>
            <div className="flex items-center gap-3 mt-6">
              {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white"><Instagram className="w-4 h-4" /></a>}
              {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white"><Mail className="w-4 h-4" /></a>}
            </div>
          </div>
          <div className="col-span-6 lg:col-span-2 lg:col-start-7">
            <div className="text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3">{t("store_shop")}</div>
            <ul className="space-y-2 text-sm text-white/70">
              {categories.slice(0, 5).map((c) => <li key={c}><Link to={`/s/${slug}/search?cat=${c}`} className="hover:text-white capitalize">{c}</Link></li>)}
            </ul>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <div className="text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3">{t("store_service")}</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to={`/s/${slug}/contact`} className="hover:text-white">{t("nav_contact")}</Link></li>
              <li><Link to="/account" className="hover:text-white">{t("nav_account")}</Link></li>
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-2">
            <div className="text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3">{t("sa_networks")}</div>
            <p className="text-xs text-white/50">{network.display_name}</p>
            <p className="text-[10px] text-white/30 mt-2">{t("store_powered_by")} TotalKlick</p>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 text-[10px] tracking-widest text-white/30 uppercase flex justify-between">
          <span>© {new Date().getFullYear()} {tenant.name}</span>
          <span>Made in Italy · Editorial Luxe</span>
        </div>
      </footer>
    </div>
  );
}
